import {
  CancelledPromiseError,
  CyclabilityZone,
  CyclabilityZoneService,
  TAdministrativeLevel,
  TGetZoneProps,
  administrativeLevelsValues,
  backendAdministrativeLevels,
  useAmplitudeTracker,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router';

import { environment } from '../../environment';
import ContentLayout from '../../layout/content';
import { getChildrenBackendLevel } from '../../utils/cyclability-zone';
import { cacheChildrenStats, cacheZonesStats, getDefaultStatsPeriod } from '../../utils/zone-stats';
import { AppContext, worldId } from '../context';

const allTabs = ['stats', 'contributions', 'comptages'] as const;

export type TTab = (typeof allTabs)[number];

export const tabs: TTab[] = allTabs.filter(
  (tab) => tab !== 'comptages' || environment.config.enableCounts === true,
);

function Tabs(): React.JSX.Element {
  const {
    zone: { map: zoneMap, codeMap: zoneCodeMap, parentMap, childrenMap },
    actions: {
      setZoneMap,
      setZoneCodeMap,
      setZoneParentMap,
      setZoneChildrenMap,
      setCountryCode,
      setZoneCode,
      setCurrentZone,
    },
  } = useContext(AppContext);
  const { pathname } = useLocation();
  const { countryCodeOrTab, zoneCodeOrTabOrId } = useParams();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { trackEvent } = useAmplitudeTracker();
  const queryClient = useQueryClient();

  useEffect(() => {
    trackEvent('Page Visited', { pathname, config: process.env.REACT_APP_CONFIG });
  }, [pathname]);

  useEffect(() => {
    const countryCode =
      countryCodeOrTab !== undefined && !tabs.find((key) => key === countryCodeOrTab)
        ? countryCodeOrTab
        : null;
    const zoneCode =
      zoneCodeOrTabOrId !== undefined && !tabs.find((key) => key === zoneCodeOrTabOrId)
        ? zoneCodeOrTabOrId
        : null;

    setCountryCode(countryCode);
    setZoneCode(zoneCode);
    update(countryCode, zoneCode);
  }, [countryCodeOrTab, zoneCodeOrTabOrId]);

  async function update(countryCode: string | null, zoneCode: string | null) {
    cancelPromises();
    setCurrentZone();

    let zone: CyclabilityZone | null | undefined;
    try {
      zone = await getZone(countryCode, zoneCode);
    } catch (err) {
      // a newer update has started: it is the one that sets the current zone
      if (err instanceof CancelledPromiseError) return;

      throw err;
    }

    setCurrentZone(zone);

    if (zone) {
      getParent(zone);
      getChildren(zone);
    }
  }

  async function getZone(
    countryCode: string | null,
    zoneCode: string | null,
  ): Promise<CyclabilityZone | null | undefined> {
    if (!countryCode)
      return environment.config.maxAdministrativeLevel === 'world' ? zoneMap[worldId] : null;

    const code = zoneCode || countryCode;
    const zoneId = zoneCodeMap[code];
    if (zoneId === null) return null;

    if (zoneId !== undefined && zoneMap[zoneId]) return zoneMap[zoneId];

    try {
      const { minAdministrativeLevel } = environment.config;
      const minAdministrativeLevelValue = administrativeLevelsValues[minAdministrativeLevel];
      const params: TGetZoneProps = { administrativeLevel: 'COUNTRY' };
      let administrativeLevel: Exclude<TAdministrativeLevel, 'world'> = 'country';

      if (!zoneCode) params.countryCode = countryCode;
      else {
        const ref = zoneCode.split('_')[1];

        if (!ref || ref.length === 0) {
          administrativeLevel = 'region';
          params.name = zoneCode;
        } else if (ref.length === 2) {
          administrativeLevel = 'department';
          params.ref = ref;
        } else if (ref.length === 9 || ref[0] === 'T') {
          administrativeLevel = 'epci';
          params.ref = ref;
        } else if (ref.length === 5) {
          administrativeLevel = 'city';
          params.ref = ref;
        } else {
          throw new Error('zone code cannot be parsed');
        }
      }

      if (minAdministrativeLevelValue > administrativeLevelsValues[administrativeLevel])
        return (zoneCodeMap[code] = null);

      params.administrativeLevel = backendAdministrativeLevels[administrativeLevel];

      const period = getDefaultStatsPeriod();
      const {
        zones: [zone],
      } = await cancellablePromise(CyclabilityZoneService.getZones({ ...params, period }));

      if (zone) {
        cacheZonesStats(queryClient, [zone], period);
        zoneMap[zone.id] = zone;
      }
      zoneCodeMap[code] = zone?.id || null;

      setZoneMap({ ...zoneMap });
      setZoneCodeMap({ ...zoneCodeMap });

      return zone;
    } catch (err) {
      if (err instanceof CancelledPromiseError) throw err;

      zoneCodeMap[code] = null;
      setZoneCodeMap({ ...zoneCodeMap });

      return null;
    }
  }

  async function getParent(zone: CyclabilityZone): Promise<void> {
    const parentId = parentMap[zone.id];
    if (parentId === null || (parentId !== undefined && zoneMap[parentId])) return;

    if (
      zone.administrativeLevel === environment.config.maxAdministrativeLevel ||
      zone.administrativeLevel === 'world'
    ) {
      parentMap[zone.id] = null;
      setZoneParentMap({ ...parentMap });

      return;
    }

    if (zone.administrativeLevel === 'country') {
      parentMap[zone.id] = worldId;
      setZoneParentMap({ ...parentMap });

      return;
    }

    try {
      const period = getDefaultStatsPeriod();
      const parent = await cancellablePromise(
        CyclabilityZoneService.getParentZone(zone, { period, query: '{-geo_polygon}' }),
      );

      if (parent) {
        cacheZonesStats(queryClient, [parent], period);
        zoneMap[parent.id] = parent;
        zoneCodeMap[parent.code] = parent.id;

        setZoneMap({ ...zoneMap });
        setZoneCodeMap({ ...zoneCodeMap });
      }

      parentMap[zone.id] = parent?.id || null;
      setZoneParentMap({ ...parentMap });
    } catch (err) {
      if (!(err instanceof CancelledPromiseError)) {
        parentMap[zone.id] = null;
        setZoneParentMap({ ...parentMap });
      }
    }
  }

  async function getChildren(zone: CyclabilityZone): Promise<void> {
    const childrenIds = childrenMap[zone.id];
    if (childrenIds !== undefined) return;

    if (
      zone.administrativeLevel === environment.config.minAdministrativeLevel ||
      zone.administrativeLevel === 'city'
    ) {
      childrenMap[zone.id] = null;
      setZoneChildrenMap({ ...childrenMap });

      return;
    }

    try {
      const administrativeLevel = getChildrenBackendLevel(zone.administrativeLevel);
      if (!administrativeLevel) throw new Error('no children administrative level');

      const period = getDefaultStatsPeriod();
      let children: CyclabilityZone[];

      if (zone.administrativeLevel === 'world') {
        ({ zones: children } = await cancellablePromise(
          CyclabilityZoneService.getZones({
            administrativeLevel,
            period,
            rowsPerPage: 100,
            query:
              '{id, country_code, code, reference, name, administrative_level, geo_polygon_simplified, stats}',
          }),
        ));
      } else {
        children = await cancellablePromise(
          CyclabilityZoneService.getChildrenZones(zone.id, {
            administrativeLevel,
            period,
            query: '{-geo_polygon}',
          }),
        );
      }

      cacheChildrenStats(queryClient, zone, administrativeLevel, children, period);

      children.forEach((child) => {
        zoneMap[child.id] = child;
        zoneCodeMap[child.code] = child.id;
      });

      setZoneMap({ ...zoneMap });
      setZoneCodeMap({ ...zoneCodeMap });

      childrenMap[zone.id] = children.map(({ id }) => id);
      setZoneChildrenMap({ ...childrenMap });
    } catch (err) {
      if (!(err instanceof CancelledPromiseError)) {
        childrenMap[zone.id] = null;
        setZoneChildrenMap({ ...childrenMap });
      }
    }
  }

  return (
    <Routes>
      <Route index element={<ContentLayout />} />
      <Route element={<ContentLayout />} path=":tabOrId" />
      <Route element={<ContentLayout />} path=":tabOrId/:id" />
    </Routes>
  );
}

export default Tabs;
